// CareersPageMNC.tsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Globe2,
  Users,
  Brain,
  Home,
  Award,
  Rocket,
  Clock,
  Coffee,
  Code2,
  Sparkles,
  Quote,
  ChevronLeft,
  ChevronRight,
  Layers,
  BarChart3,
  Cloud,
  Database,
  GitBranch,
  MonitorSmartphone,
  CheckCircle2,
} from "lucide-react";

// -----------------------------------------
// Employee Testimonials (slider component)
// -----------------------------------------
function EmployeeTestimonialsSection(): JSX.Element {
  const testimonials = [
    {
      name: "Aditi Sharma",
      title: "Data Scientist, Fintech Squad",
      quote:
        "At Technocolabs I moved from toy datasets to shipping risk models for real customers. The mentorship and code reviews pushed my skills to another level.",
      location: "Remote · Pune, India",
    },
    {
      name: "Rahul Verma",
      title: "Full-Stack Engineer, Product Labs",
      quote:
        "You get ownership here. I’ve led features end-to-end — from API design to production deploys — within my first year.",
      location: "Hybrid · Indore, India",
    },
    {
      name: "Sara Malik",
      title: "AI Intern → ML Engineer",
      quote:
        "My internship wasn’t just tasks — I worked on client PoCs that turned into real projects, which helped me convert to a full-time ML role.",
      location: "Remote · Bengaluru, India",
    },
  ];

  const [index, setIndex] = React.useState(0);
  const current = testimonials[index];

  const goPrev = () =>
    setIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  const goNext = () =>
    setIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));

  return (
    <section className="bg-[#0a2540] py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 border border-white/10 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">
              <Quote className="h-3.5 w-3.5" />
              Employee Stories
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold">
              What our teammates say
            </h2>
            <p className="mt-2 text-sm sm:text-base text-white/70 max-w-xl">
              Hear from people who started as interns, early hires, and
              experienced engineers building AI products with us.
            </p>
          </div>

          {/* controls */}
          <div className="flex items-center gap-3 self-end md:self-center">
            <button
              type="button"
              onClick={goPrev}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg:white/5 bg-white/5 hover:bg-white/10 transition"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/30 bg-white/5 hover:bg-white/10 transition"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* card */}
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-white/8 via-white/5 to-white/0 border border-white/15 p-6 sm:p-8 shadow-lg">
          <p className="text-sm sm:text-lg leading-relaxed text-white/90">
            “{current.quote}”
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm sm:text-base font-semibold">
                {current.name}
              </div>
              <div className="text-xs sm:text-sm text-sky-200">
                {current.title}
              </div>
              <div className="text-[0.7rem] sm:text-xs text-sky-300 mt-0.5">
                {current.location}
              </div>
            </div>

            {/* dots */}
            <div className="flex items-center gap-1.5">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={`h-2.5 rounded-full transition-all ${
                    i === index ? "w-6 bg-sky-400" : "w-2 bg-white/30"
                  }`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Career Tracks section
// -----------------------------------------
function CareerTracksSection(): JSX.Element {
  const tracks = [
    {
      title: "AI & Machine Learning",
      icon: Brain,
      highlight: "Models, MLOps, GenAI",
      items: [
        "Build and fine-tune production ML models",
        "Experiment with LLMs, NLP, CV & RAG",
        "Work with MLOps pipelines & monitoring",
      ],
    },
    {
      title: "Data & Analytics",
      icon: BarChart3,
      highlight: "Data Engineering & BI",
      items: [
        "Design ETL pipelines and data platforms",
        "Build dashboards & metrics for leadership",
        "Work with SQL, warehouses and cloud",
      ],
    },
    {
      title: "Full-Stack Engineering",
      icon: MonitorSmartphone,
      highlight: "Product Engineering",
      items: [
        "Ship web apps end-to-end with real users",
        "Work across frontend, backend & APIs",
        "Focus on performance, DX and quality",
      ],
    },
    {
      title: "Cloud, DevOps & Infra",
      icon: Cloud,
      highlight: "Scalable Systems",
      items: [
        "Deploy services on AWS / GCP",
        "Automate with CI/CD, containers & IaC",
        "Own reliability, cost and security",
      ],
    },
  ];

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-4 py-1.5 border border-slate-200 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700">
            <Layers className="h-3.5 w-3.5" />
            Career Tracks
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
            Choose a track that fits your ambition
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
            Whether you love models, data, product or infrastructure — you’ll find a
            track where you can grow fast and ship meaningful work.
          </p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {tracks.map(({ title, icon: Icon, highlight, items }) => (
            <div
              key={title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-sky-50 via-transparent to-indigo-50" />
              <div className="relative flex flex-col gap-3">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-50 text-sky-600 border border-sky-100">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-[#0a2540]">
                  {title}
                </h3>
                <p className="text-xs font-semibold text-sky-600">
                  {highlight}
                </p>
                <ul className="mt-2 space-y-1.5 text-xs sm:text-sm text-[#0a2540]/70">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-1.5">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-sky-500" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Growth Path / Levels section
// -----------------------------------------
function GrowthPathSection(): JSX.Element {
  const levels = [
    {
      label: "Intern",
      years: "0–1 yrs",
      width: "35%",
      focus: "Foundations, mentorship, real projects",
    },
    {
      label: "Junior Engineer",
      years: "1–2 yrs",
      width: "50%",
      focus: "Own tasks, contribute to features",
    },
    {
      label: "Engineer",
      years: "2–4 yrs",
      width: "70%",
      focus: "Own modules, ship to production confidently",
    },
    {
      label: "Senior Engineer",
      years: "4–7 yrs",
      width: "85%",
      focus: "Lead projects, mentor others, drive decisions",
    },
    {
      label: "Tech Lead / Architect",
      years: "7+ yrs",
      width: "100%",
      focus: "Set direction, architecture & technical strategy",
    },
  ];

  return (
    <section className="bg-[#f7f9fb] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 border border-slate-200 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700">
            <GitBranch className="h-3.5 w-3.5" />
            Growth at Technocolabs
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
            See your growth path clearly
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
            From intern to tech lead — we invest in long-term growth, not just short-term output.
          </p>
        </div>

        <div className="mt-10 rounded-2xl bg-white border border-slate-200/80 p-6 sm:p-8 shadow-sm">
          <div className="space-y-4">
            {levels.map((lvl) => (
              <div key={lvl.label} className="space-y-1">
                <div className="flex items-center justify-between text-xs sm:text-sm text-[#0a2540]/80">
                  <span className="font-semibold">{lvl.label}</span>
                  <span className="text-[#0a2540]/60">{lvl.years}</span>
                </div>
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-sky-400 to-indigo-500 transition-all duration-500"
                    style={{ width: lvl.width }}
                  />
                </div>
                <p className="text-xs sm:text-sm text-[#0a2540]/65">
                  {lvl.focus}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Culture Gallery section
// -----------------------------------------
function CultureGallerySection(): JSX.Element {
  const items = [
    {
      title: "Hackathons & Ship Weeks",
      desc: "Teams sprint on ideas, prototypes and internal tools.",
      src: "/Hack.jpg",
    },
    {
      title: "Tech Talks & Learning Circles",
      desc: "Weekly deep-dives into AI, data, and engineering topics.",
      src: "/tech-talk.jpg",
    },
    {
      title: "Hybrid Collaboration",
      desc: "Remote-first but with in-person jams when it matters.",
      src: "/hybrid.webp",
    },
    {
      title: "Celebrations & Offsites",
      desc: "We celebrate launches, milestones and people.",
      src: "/offsite.jpg",
    },
  ];

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-4 py-1.5 border border-slate-200 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700">
            <Sparkles className="h-3.5 w-3.5" />
            Culture & Life
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
            A culture built around learning & impact
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
            We mix serious engineering work with a culture that’s friendly, curious,
            and always shipping.
          </p>
        </div>

        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <div
              key={item.title}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={item.src}
                  alt={item.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-80" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-left text-white">
                <h3 className="text-sm sm:text-base font-semibold">
                  {item.title}
                </h3>
                <p className="mt-1 text-xs sm:text-sm text-white/80">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Hiring Process section
// -----------------------------------------
function HiringProcessSection(): JSX.Element {
  const steps = [
    {
      label: "Apply",
      desc: "Share your resume, GitHub, portfolio or LinkedIn.",
    },
    {
      label: "Screening",
      desc: "We review your profile and shortlist based on fit.",
    },
    {
      label: "Skills Assessment",
      desc: "Small assignment or live problem-solving round.",
    },
    {
      label: "Technical Interview",
      desc: "Deep-dive into your projects, decisions and thinking.",
    },
    {
      label: "Culture Fit",
      desc: "Meet the team and understand how we work together.",
    },
    {
      label: "Offer",
      desc: "If it’s a match, we move fast with a clear offer.",
    },
  ];

  return (
    <section className="bg-[#f7f9fb] py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 border border-slate-200 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Hiring Process
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
            How we hire at Technocolabs
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
            Transparent, structured, and designed to understand how you think — not
            just how fast you code.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-3 lg:grid-cols-6">
          {steps.map((step, idx) => (
            <div
              key={step.label}
              className="relative flex flex-col items-center text-center rounded-2xl bg-white border border-slate-200/80 p-4 shadow-sm"
            >
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-sky-50 border border-sky-200 text-xs font-semibold text-sky-700">
                {idx + 1}
              </div>
              <h3 className="mt-3 text-sm font-semibold text-[#0a2540]">
                {step.label}
              </h3>
              <p className="mt-1 text-xs text-[#0a2540]/70">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Tech Stack section
// -----------------------------------------
function TechStackSection(): JSX.Element {
  const stackGroups = [
    {
      label: "AI & Data",
      icon: Database,
      items: ["Python", "PyTorch", "TensorFlow", "Hugging Face", "Pandas"],
    },
    {
      label: "Backend & APIs",
      icon: Code2,
      items: ["FastAPI", "Node.js", "PostgreSQL", "Redis", "gRPC / REST"],
    },
    {
      label: "Frontend & Apps",
      icon: MonitorSmartphone,
      items: ["React", "TypeScript", "Next.js", "Tailwind CSS"],
    },
    {
      label: "Cloud & DevOps",
      icon: Cloud,
      items: ["AWS", "GCP", "Docker", "Kubernetes", "GitHub Actions"],
    },
  ];

  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-4 py-1.5 border border-slate-200 text-xs font-semibold uppercase tracking-[0.2em] text-slate-700">
            <Database className="h-3.5 w-3.5" />
            Tech We Use
          </div>
          <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
            Work with a modern AI & engineering stack
          </h2>
          <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
            Get hands-on experience with tools, frameworks and platforms that are in
            demand at top product companies.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stackGroups.map(({ label, icon: Icon, items }) => (
            <div
              key={label}
              className="rounded-2xl border border-slate-200 bg-[#f7f9fb] p-5 shadow-sm"
            >
              <div className="flex items-center gap-2">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white text-sky-600 border border-sky-100">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="text-sm font-semibold text-[#0a2540]">
                  {label}
                </h3>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {items.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-[#0a2540]/80"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -----------------------------------------
// Main Careers Page
// -----------------------------------------
export default function CareersPageMNC(): JSX.Element {
  const navigate = useNavigate();

  // internships now have track + mode (for remote filter + badges)
  const internships: { title: string; track: string; mode: string }[] = [
    {
      title: "AI Engineer Intern",
      track: "AI & ML",
      mode: "Remote / Hybrid",
    },
    {
      title: "Business Intelligence Developer Intern",
      track: "Data & Analytics",
      mode: "Remote / On-site",
    },
    {
      title: "Computer Vision Engineer Intern",
      track: "AI & ML",
      mode: "Remote",
    },
    {
      title: "Data Analyst Intern",
      track: "Data & Analytics",
      mode: "Remote / Hybrid",
    },
    {
      title: "Data Scientist Intern",
      track: "AI & ML",
      mode: "Remote",
    },
    {
      title: "Deep Learning Engineer Intern",
      track: "AI & ML",
      mode: "Remote / On-site",
    },
    {
      title: "Full Stack Developer Intern",
      track: "Full-Stack",
      mode: "Hybrid / On-site",
    },
    {
      title: "Machine Learning Engineer Intern",
      track: "AI & ML",
      mode: "Remote",
    },
    {
      title: "Python Developer Intern",
      track: "Backend",
      mode: "Remote / Hybrid",
    },
  ];

  const ROLE_TITLE_TO_ID: Record<string, string> = {
    "AI Engineer Intern": "genai",
    "Business Intelligence Developer Intern": "bi",
    "Computer Vision Engineer Intern": "cv",
    "Data Analyst Intern": "da",
    "Data Scientist Intern": "ds",
    "Deep Learning Engineer Intern": "dl",
    "Full Stack Developer Intern": "web",
    "Machine Learning Engineer Intern": "ml",
    "Python Developer Intern": "py",
  };

  const ROLE_ID_TO_PRETTY: Record<string, string> = {
    genai: "AI Engineer",
    bi: "Business Intelligence Developer",
    cv: "Computer Vision Engineer",
    da: "Data Analyst",
    ds: "Data Scientist",
    dl: "Deep Learning Engineer",
    web: "Full Stack Developer",
    ml: "Machine Learning Engineer",
    py: "Python Developer",
  };

  const openJobDescription = (title: string) => {
    const id = ROLE_TITLE_TO_ID[title];
    if (id) navigate(`/job-description/${id}`);
    else navigate(`/job-description`);
  };

  const applyDirect = (id?: string) => {
    const pretty = id ? ROLE_ID_TO_PRETTY[id] ?? "" : "";
    if (pretty)
      navigate(`/internship-apply?role=${encodeURIComponent(pretty)}`);
    else navigate(`/internship-apply`);
  };

  const heroRef = React.useRef<HTMLElement | null>(null);
  const internshipsRef = React.useRef<HTMLElement | null>(null);
  const fulltimeRef = React.useRef<HTMLElement | null>(null);

  // which tab is active in Open Positions
  const [positionsTab, setPositionsTab] =
    React.useState<"internships" | "fulltime">("internships");

  // remote-only filter
  const [remoteOnly, setRemoteOnly] = React.useState(false);

  const visibleInternships =
    remoteOnly
      ? internships.filter((i) =>
          i.mode.toLowerCase().includes("remote")
        )
      : internships;

  // track badge config (color + icon)
  type TrackConfig = {
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    classes: string;
  };

  const getTrackConfig = (track: string): TrackConfig => {
    switch (track) {
      case "AI & ML":
        return {
          label: "AI & ML",
          Icon: Brain,
          classes:
            "border-sky-400/50 bg-sky-500/20 text-sky-50",
        };
      case "Data & Analytics":
        return {
          label: "Data & Analytics",
          Icon: BarChart3,
          classes:
            "border-emerald-400/50 bg-emerald-500/20 text-emerald-50",
        };
      case "Full-Stack":
        return {
          label: "Full-Stack",
          Icon: MonitorSmartphone,
          classes:
            "border-violet-400/50 bg-violet-500/20 text-violet-50",
        };
      case "Backend":
        return {
          label: "Backend",
          Icon: Code2,
          classes:
            "border-amber-400/50 bg-amber-500/20 text-amber-50",
        };
      default:
        return {
          label: track,
          Icon: Sparkles,
          classes:
            "border-slate-400/50 bg-slate-500/20 text-slate-50",
        };
    }
  };

  function scrollToRef(ref: React.RefObject<HTMLElement>) {
    const el = ref.current;
    if (!el) {
      const id =
        ref === internshipsRef
          ? "#internships"
          : ref === fulltimeRef
          ? "#fulltime"
          : null;
      if (id) {
        const fallback = document.querySelector(id);
        if (fallback)
          (fallback as HTMLElement).scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
      }
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    try {
      el.setAttribute("tabindex", "-1");
      el.focus();
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] text-[#0a2540]">
      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-white pt-24 sm:pt-28 pb-16 sm:pb-20"
        aria-label="Careers hero"
      >
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#e6f0ff] via-white to-white" />
          <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#1e90ff]/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-sky-300/20 blur-3xl" />
          <div
            className="absolute inset-x-8 top-24 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent"
            aria-hidden
          />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-[#1e90ff]/20 px-4 py-1.5 shadow-sm mb-4">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[0.75rem] font-semibold uppercase tracking-[0.22em] text-[#0a2540]/70">
              We’re hiring across AI, Data &amp; Engineering
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-[#0a2540]">
            Build Your Career at{" "}
            <span className="bg-gradient-to-r from-[#1e90ff] to-[#5aa5ff] bg-clip-text text-transparent">
              Technocolabs
            </span>
          </h1>

          <p className="mt-4 text-base sm:text-lg text-[#0a2540]/80 max-w-2xl mx-auto">
            Join a global team building AI, Data, and Cloud solutions used by
            enterprises worldwide. Learn, grow, and innovate with real impact on
            products that ship to production.
          </p>

          <div className="mt-5 flex flex-wrap justify-center gap-3 text-xs sm:text-sm text-[#0a2540]/65">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 border border-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Mentorship from senior engineers
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 border border-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
              Real-world client projects
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 border border-slate-200">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              Remote–friendly, flexible culture
            </span>
          </div>

          <div className="mt-8 flex justify-center gap-3 sm:gap-4 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setPositionsTab("internships");
                scrollToRef(internshipsRef);
              }}
              className="px-6 sm:px-7 py-3 rounded-full bg-[#1e90ff] text-white text-sm sm:text-base font-semibold shadow-md hover:shadow-lg hover:bg-[#1975d0] transition"
            >
              Explore Internships
            </button>

            <button
              type="button"
              onClick={() => {
                setPositionsTab("fulltime");
                scrollToRef(fulltimeRef);
              }}
              className="px-6 sm:px-7 py-3 rounded-full border border-[#1e90ff]/60 bg-white/90 text-[#0a2540] text-sm sm:text-base font-semibold shadow-sm hover:shadow-md hover:bg-[#e8f2ff] transition"
            >
              View Full-Time Roles
            </button>

            <button
              type="button"
              onClick={() => navigate("/spotlight")}
              className="px-6 sm:px-7 py-3 rounded-full bg-[#0a2540] text-white text-sm sm:text-base font-semibold shadow-sm hover:shadow-md hover:bg-[#07172b] transition"
            >
              Employee Spotlight
            </button>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="relative bg-[#f7f9fb] py-16 sm:py-20">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-24 -left-10 h-64 w-64 rounded-full bg-sky-200/40 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-72 w-72 rounded-full bg-blue-300/35 blur-3xl" />
        </div>

        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-sky-200 px-4 py-1.5 shadow-sm">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[0.7rem] font-semibold tracking-[0.22em] text-sky-700 uppercase">
                Life at Technocolabs
              </span>
            </div>

            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
              Why Work With <span className="text-[#1e90ff]">Technocolabs</span>
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
              A place where you ship production work, grow with mentors, and stay
              close to real-world AI problems.
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs sm:text-sm text-[#0a2540]/70">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm border border-slate-200">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                10,000+ developers trained
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm border border-slate-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Teams across 15+ countries
              </div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm border border-slate-200">
                <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
                Remote, hybrid &amp; on-site roles
              </div>
            </div>
          </div>

          <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Work With Global Clients",
                desc: "Solve problems for enterprises, startups, and research teams across 15+ countries.",
                icon: Globe2,
              },
              {
                title: "Mentorship From Experts",
                desc: "Learn directly from senior engineers, data scientists and solution architects.",
                icon: Users,
              },
              {
                title: "AI-First Environment",
                desc: "Be part of cutting-edge work in AI, ML, automation, NLP and data platforms.",
                icon: Brain,
              },
              {
                title: "Flexible Work Options",
                desc: "Remote, hybrid, and on-site setups depending on project and team needs.",
                icon: Home,
              },
              {
                title: "Certificates & Recognition",
                desc: "Earn credentials, internal badges and public recognition for your impact.",
                icon: Award,
              },
              {
                title: "Real Project Exposure",
                desc: "Deploy models, build apps, and contribute to production-grade systems.",
                icon: Rocket,
              },
            ].map(({ title, desc, icon: Icon }) => (
              <div
                key={title}
                className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-sky-50 via-transparent to-indigo-50" />
                <div className="relative flex flex-col gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 border border-sky-100 shadow-sm group-hover:bg-sky-100 group-hover:text-sky-700 transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-[#0a2540]">
                    {title}
                  </h3>
                  <p className="text-sm text-[#0a2540]/70 leading-relaxed">
                    {desc}
                  </p>
                  <div className="mt-3 text-xs font-semibold text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn more in our onboarding deck →
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Tracks */}
      <CareerTracksSection />

      {/* Growth Path */}
      <GrowthPathSection />

      {/* Day in the Life */}
      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-4 py-1.5 border border-sky-100 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
              <Clock className="h-3.5 w-3.5" />
              A Day in the Life
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold text-[#0a2540]">
              What your day at Technocolabs looks like
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-[#0a2540]/70">
              A balance of deep work, collaboration, and learning — whether you’re
              remote, hybrid or on-site.
            </p>
          </div>

          <div className="mt-12 grid gap-8 lg:grid-cols-[1fr,1.2fr] items-start">
            <div className="self-center rounded-2xl bg-[#0a2540] text-white p-6 sm:p-7 shadow-lg">

              <h3 className="text-lg font-semibold">How we work</h3>
              <p className="mt-2 text-sm text-white/80">
                Most teams work in 2–week sprints with daily standups, async updates
                and weekly demos. You’ll collaborate with mentors, PMs and other
                engineers on real client projects.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/80">
                <li>• Flexible core hours with remote-friendly rituals</li>
                <li>• No meeting afternoons twice a week for deep work</li>
                <li>• Pair programming for tricky problems</li>
                <li>• Demo Fridays to show what you’ve shipped</li>
              </ul>
            </div>

            <ol className="relative border-l border-slate-200 pl-5 sm:pl-6 space-y-8">
              {[
                {
                  time: "10:00 – 10:20",
                  title: "Team standup",
                  icon: Users,
                  desc: "Quick async check-ins and a short video standup to align on priorities, blockers and pairings.",
                },
                {
                  time: "10:30 – 13:00",
                  title: "Deep work on features",
                  icon: Code2,
                  desc: "Focus time to design APIs, build pipelines, train models or ship UI flows — no random meetings.",
                },
                {
                  time: "13:00 – 14:00",
                  title: "Lunch & async reviews",
                  icon: Coffee,
                  desc: "Grab lunch, review PRs, leave comments and catch up on product discussions at your pace.",
                },
                {
                  time: "14:00 – 16:00",
                  title: "Collaboration & pairing",
                  icon: Users,
                  desc: "Pair with another engineer, PM or data scientist to debug, tune models, or refine product flows.",
                },
                {
                  time: "16:00 – 18:00",
                  title: "Learning & demos",
                  icon: Sparkles,
                  desc: "Join an internal tech talk, contribute to RFCs, or present what you shipped in demo sessions.",
                },
              ].map(({ time, title, icon: Icon, desc }) => (
                <li key={title} className="relative pl-6">
                  <span className="absolute -left-[9px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white border border-sky-400">
                    <span className="h-2 w-2 rounded-full bg-sky-500" />
                  </span>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600 border border-sky-100">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">
                        {time}
                      </p>
                      <h3 className="text-sm sm:text-base font-semibold text-[#0a2540]">
                        {title}
                      </h3>
                      <p className="mt-1 text-xs sm:text-sm text-[#0a2540]/70">
                        {desc}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Culture Gallery */}
      <CultureGallerySection />

      {/* Testimonials */}
      <EmployeeTestimonialsSection />

      {/* OPEN POSITIONS (Internships + Full-time in tabs) */}
      <section
        id="open-positions"
        className="bg-[#020617] text-white py-16 sm:py-20"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 border border-white/10 text-xs font-semibold uppercase tracking-[0.2em] text-sky-200">
              Open Positions
            </div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold">
              Roles at Technocolabs
            </h2>
            <p className="mt-3 max-w-2xl mx-auto text-sm sm:text-base text-white/70">
              Explore current internship tracks and upcoming full-time roles across
              AI, data and engineering.
            </p>
          </div>

          {/* Tabs */}
          <div className="mt-8 flex justify-center">
            <div className="inline-flex rounded-full bg-white/5 p-1 border border-white/10">
              <button
                type="button"
                onClick={() => setPositionsTab("internships")}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm rounded-full font-semibold transition ${
                  positionsTab === "internships"
                    ? "bg-white text-[#020617]"
                    : "text-white/70 hover:text-white"
                }`}
              >
                Internship Positions
              </button>
              <button
                type="button"
                onClick={() => setPositionsTab("fulltime")}
                className={`px-4 sm:px-6 py-1.5 sm:py-2 text-xs sm:text-sm rounded-full font-semibold transition ${
                  positionsTab === "fulltime"
                    ? "bg-white text-[#020617]"
                    : "text-white/70 hover:text-white"
                }`}
              >
                Full-Time Roles
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="mt-8">
            {/* invisible anchors so scrollToRef still works */}
            <div ref={internshipsRef} id="internships" className="-mt-20 pt-20" />

            {positionsTab === "internships" && (
              <>
                {/* Remote-only toggle */}
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-xs sm:text-sm text-white/70">
                    Showing internship openings across multiple tracks. Use the filter to
                    see only roles with remote options.
                  </p>
                  <button
                    type="button"
                    onClick={() => setRemoteOnly((v) => !v)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs sm:text-sm transition ${
                      remoteOnly
                        ? "border-emerald-400 bg-emerald-500/20 text-emerald-50"
                        : "border-white/30 bg-white/5 text-white/80 hover:bg-white/10"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        remoteOnly ? "bg-emerald-400" : "bg-slate-400"
                      }`}
                    />
                    Remote only
                  </button>
                </div>

                {visibleInternships.length === 0 ? (
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-6 text-sm text-white/75">
                    No internship roles are currently marked as remote-only. Turn off the
                    filter to see all openings.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {visibleInternships.map(({ title, track, mode }) => {
                      const id = ROLE_TITLE_TO_ID[title];
                      const { label, Icon, classes } = getTrackConfig(track);
                      return (
                        <div
                          key={title}
                          className="rounded-2xl border border-white/10 bg-white/5 p-5 shadow-sm hover:shadow-lg hover:border-sky-400/60 transition"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-lg">
                              {id ? (
                                <Link
                                  to={`/job-description/${id}`}
                                  className="hover:underline"
                                >
                                  {title.replace(/ Intern$/i, "")}
                                </Link>
                              ) : (
                                <span>{title}</span>
                              )}
                            </h3>
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ${classes}`}
                            >
                              <Icon className="h-3 w-3" />
                              {label}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-white/70">
                            8–12 weeks • {mode}
                          </p>
                          <div className="mt-4 flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => openJobDescription(title)}
                              className="text-sky-300 font-medium hover:underline text-sm"
                            >
                              Job Description →
                            </button>
                            <button
                              type="button"
                              onClick={() => applyDirect(id)}
                              className="ml-auto inline-flex items-center gap-2 rounded-lg bg-sky-500 px-3 py-2 text-sm text-white font-semibold hover:bg-sky-400 transition"
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            <div ref={fulltimeRef} id="fulltime" className="-mt-20 pt-20" />

            {positionsTab === "fulltime" && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8 shadow-sm max-w-3xl mx-auto">
                <h3 className="text-xl font-semibold">Full-Time Roles (Coming Soon)</h3>
                <p className="mt-2 text-sm sm:text-base text-white/75">
                  We’ll soon open full-time roles across AI, data, engineering and
                  product. Share your resume now and we’ll reach out when a matching
                  position opens.
                </p>
                <a
                  href="mailto:contact@technocolabs.com?subject=Full-Time Application"
                  className="mt-5 inline-flex items-center px-5 py-3 rounded-xl bg-sky-500 text-white font-semibold shadow hover:shadow-lg hover:bg-sky-400 transition"
                >
                  Email Resume
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <HiringProcessSection />

      {/* Tech Stack */}
      <TechStackSection />

      {/* CTA */}
      <section className="bg-[#0a2540] text-white py-16 border-b-2 border-white">
        <div className="mx-auto max-w-7xl px-6 text-center">
          <h3 className="text-3xl font-bold">Ready to Start Your Journey?</h3>
          <p className="mt-2 text-white/70 max-w-xl mx-auto">
            Build impactful AI and engineering projects with a world-class team.
          </p>
          <button
            type="button"
            onClick={() => {
              setPositionsTab("internships");
              scrollToRef(internshipsRef);
            }}
            className="mt-6 px-6 py-3 rounded-xl bg-[#1e90ff] text-white font-semibold shadow hover:shadow-xl"
          >
            Apply for Internships
          </button>
        </div>
      </section>
    </div>
  );
}
